/**
 * SpriteLayer — manages Phaser.GameObjects.Image objects for the static/semi-static
 * game entities: relay control nodes, HQ cores, and buildings (turrets + hubs).
 *
 * Images are created lazily on the first sync call for each entity, then kept alive
 * and updated (frame, alpha, position) each render cycle. HP bars, selection rings,
 * progress bars, and turret barrels are still drawn on top by the Graphics-based
 * Renderer layers — SpriteLayer only owns the sprite body.
 *
 * Atlas texture keys match the keys loaded in BootScene.preload().
 * Frame names match the JSON manifests generated by tools/gen-atlas-frames.mjs.
 */

import type { Building, MatchState, NeutralUnit, PlayerId, Projectile, Squad } from '@signalclash/shared';
import { isOperational } from '@signalclash/shared';
import Phaser from 'phaser';

// ---------------------------------------------------------------------------
// Display dimensions (world pixels, uniform for each entity kind).
//
// V1.5+ asset-hierarchy rescale. Before this pass, the HQ Core was only ~30%
// taller than a Recon squad and relays were peer-sized to a 4-member Assault.
// The eye couldn't instantly tell unit / building / objective apart, so the
// world read as a model railway. New scale ratios (relative to a Recon
// squad's ~57 px display size):
//
//   Recon squad   1.00× baseline
//   Assault squad 1.20× heavier
//   Shield squad  1.30× most distinct silhouette
//   Mini-turret   1.05× small forward defence
//   Permanent     1.65× a real defensive landmark
//   Hub           ~2.0× mid-importance landmark
//   Relay         ~2.3× the war is fought over these — they dominate
//   HQ Core       3.5×  a fortress — your win condition
//
// Picking radii live in input/Pickers.ts. This file only changes the visual
// display size of the sprite under those overlays.
// ---------------------------------------------------------------------------
// V1.8 — bumped 130→160 after removing decorative pylons. The pylons
// had been "softening" the Relay's apparent size by sitting next to it
// in the same visual vocabulary; now that they're gone the Relay is on
// its own and needs a hair more presence so it doesn't read as
// background dressing.
const RELAY_W = 160;
const RELAY_H = 205;   // preserves relay_neutral aspect ratio (~0.78)
// V1.8 — Core is the win/lose target; CrazyGames playtester reported the
// surrounding Relays looked more dominant than the Core itself, which is
// the worst possible visual hierarchy. Bumped from 200×255 → 320×410
// (≈2.5× a Relay's footprint linearly, ≈6× by area) so the Core reads
// as a fortress at a glance. Canvas atlas size in genHQ grew in lockstep
// (224×286) to fit the new outer fortress wall ring + cardinal turrets;
// display size keeps the same ~0.78 aspect ratio as the atlas frame.
const CORE_W  = 320;
const CORE_H  = 410;
const TURRET_W = 95;   // ↑ from 72 — a real defensive landmark
const TURRET_H = 95;   // preserves turret_p0 aspect ratio (341×331 → ~1.03)
const MINI_W  = 60;    // ↑ from 50 — readable at 0.5× zoom
const MINI_H  = 75;    // preserves mini_active aspect ratio (342×429 → ~0.80)
// V1.8.1 — hub display size reduced (was 115×170) so hubs sit clearly
// below relays (130×165) in the visual hierarchy. Hubs are support
// structures; they should NOT compete with relays for attention.
const HUB_W   = 90;
const HUB_H   = 95;

// Sprite origin: top-down entities are centered on their world position.
// Both X and Y use 0.5 so the sprite is anchored at its visual centre.
const ORIGIN_X = 0.5;
const ORIGIN_Y = 0.5;

// Atlas texture keys
const TEX_RELAYS = 'relays';
const TEX_HQ     = 'hq';
const TEX_TURRET = 'turret_def';
const TEX_MINI   = 'turret_mini';
const TEX_HUB    = 'production';
const TEX_FX     = 'fx';
const TEX_POLISH = 'polish';
// V1.8 facelift — dedicated SQUARE atlas for light pools and capture rings.
// Lives separately from polish (which has a 200×120 elongated frame layout
// optimised for shadows/portals).
const TEX_POOLS  = 'pools';
const TEX_SQUADS = 'squads';
const TEX_CAMP   = 'camp_units';

// Display size for the neutral camp turret sprite — matches player permanent
// turret scale so both defensive emplacements read at the same weight.
const CAMP_TURRET_W = 72;
const CAMP_TURRET_H = 70;

// Display size for the neutral patrol drone sprite — sits between a single
// player squad member and a player turret; ~52 px so it reads as a notable
// hostile unit rather than a peer infantry blob.
const CAMP_PATROL_W = 52;
const CAMP_PATROL_H = 52;

// Display sizes for fx / polish effects.
// V1.5+ rescale: portals + shadows scale up to match the new fortress-sized
// HQs and monumental relays. Bullets / tracers stay the same — projectile
// scale is tuned for combat readability and decouples from building scale.
// V1.8 — portal aura scales with the new fortress-sized Core (320→460
// wide). The aura is what bleeds onto the surrounding terrain and tells
// the player "this is the structure under siege" from the minimap-scale
// perspective; if it stays at the old 320 the Core appears to "float"
// on a now-too-small ground glow.
const PORTAL_W        = 480;
const PORTAL_H        = 300;
// V1.8 — relay shadow scales with the bumped Relay sprite (130→160 wide).
const RELAY_SHADOW_W  = 215;
const RELAY_SHADOW_H  = 110;
const BLDG_SHADOW_W   = 130;  // drop shadow under buildings  (was  90)
const BLDG_SHADOW_H   =  65;  //                              (was  45)
// V1.8 facelift — team-color light-pool sizes. Pools are additive; the
// numbers below define how far the "powered" glow spills onto the floor.
// Sized generously so the pool feels like genuine illumination spill,
// not a thin halo. Rings are tighter — they're a captured-territory stamp.
const RELAY_POOL_W    = 230;
const RELAY_POOL_H    = 230;
const RELAY_RING_W    = 165;
const RELAY_RING_H    =  95;  // shorter Y = "floor stamp" perspective
// V1.8.1 — hub pool tightened (was 200) so the team-colour spill matches the
// new smaller hub footprint and doesn't bleed past nearby cover.
const HUB_POOL_W      = 130;
const HUB_POOL_H      = 130;
const TURRET_POOL_W   = 165;
const TURRET_POOL_H   = 165;
const MINI_POOL_W     = 105;
const MINI_POOL_H     = 105;
// Bullet/tracer sizes — restored to the original intentional values after
// the V1.8 shell-casing fix removed the "floating debris" effect. The
// brown football shapes that motivated a short-lived size reduction were
// actually shell casings (now properly sized in EffectsLayer), not these
// energy tracers. The "longer, more punch" sizing reads correctly when
// the tracers fly cleanly between unit and target.
const BULLET_W        =  44;  // projectile bullet sprite (longer, more punch)
const BULLET_H        =  10;
const TRACER_W        =  56;  // additive trail behind bullet
const TRACER_H        =  14;
// V1.6 — bumped from 57 to 76 after playtest. The original 57 px diameter
// made friendly squads near-invisible against the busy hex background; the
// new size still leaves room for HP bar overlays without crowding.
const SQUAD_BASE_PX   =  76;

// ---------------------------------------------------------------------------
// Frame-selection helpers
// ---------------------------------------------------------------------------

function relayFrame(ownerId: PlayerId | null, contested: boolean): string {
  if (contested && ownerId === null) return 'relay_teal';   // neutral but being fought over
  if (ownerId === 0) return 'relay_p0';
  if (ownerId === 1) return 'relay_p1';
  return 'relay_neutral';
}

function coreFrame(ownerId: PlayerId | null, hp: number, maxHp: number): string {
  if (hp / maxHp < 0.30) return 'hq_damaged';
  if (ownerId === 1) return 'hq_p1';
  return 'hq_p0';  // cores always have an owner; fall back to p0
}

function buildingFrame(b: Building): string {
  const op = isOperational(b);
  if (b.kind === 'production_hub') {
    if (!op) return 'hub_idle';
    return b.ownerId === 1 ? 'hub_p1' : 'hub_p0';
  }
  if (b.kind === 'mini_turret') {
    return op ? 'mini_active' : 'mini_idle';
  }
  // permanent turret
  if (!op) return 'turret_idle';
  return b.ownerId === 1 ? 'turret_p1' : 'turret_p0';
}

function buildingTex(kind: Building['kind']): string {
  if (kind === 'production_hub') return TEX_HUB;
  if (kind === 'mini_turret')   return TEX_MINI;
  return TEX_TURRET;
}

function buildingSize(kind: Building['kind']): { w: number; h: number } {
  if (kind === 'production_hub') return { w: HUB_W,    h: HUB_H    };
  if (kind === 'mini_turret')   return { w: MINI_W,   h: MINI_H   };
  return                                { w: TURRET_W, h: TURRET_H };
}

// Mirrors the sin-wave flicker used by Renderer for offline mini-turrets.
function offlineMiniTurretAlpha(nowMs: number): number {
  return 0.4 + 0.05 * Math.sin((nowMs / 1000) * Math.PI * 6);
}

// ---------------------------------------------------------------------------
// SpriteLayer
// ---------------------------------------------------------------------------

export class SpriteLayer {
  private relayImgs         = new Map<number, Phaser.GameObjects.Image>();
  private coreImgs          = new Map<number, Phaser.GameObjects.Image>();
  private buildingImgs      = new Map<number, Phaser.GameObjects.Image>();

  // Polish / FX layers
  private coreAuraImgs      = new Map<number, Phaser.GameObjects.Image>();
  private relayShadowImgs   = new Map<number, Phaser.GameObjects.Image>();
  private buildingShadowImgs = new Map<number, Phaser.GameObjects.Image>();
  // V1.8 facelift — team-color light pools spilled onto the floor under
  // owned relays + operational owned buildings. Additive blend; only shown
  // when the structure has a clear owner (neutral relays get no pool).
  private relayPoolImgs     = new Map<number, Phaser.GameObjects.Image>();
  private relayRingImgs     = new Map<number, Phaser.GameObjects.Image>();
  private buildingPoolImgs  = new Map<number, Phaser.GameObjects.Image>();
  private projectileImgs    = new Map<number, Phaser.GameObjects.Image>();
  private projectileTrails  = new Map<number, Phaser.GameObjects.Image>();
  private squadImgs         = new Map<number, Phaser.GameObjects.Image>();
  private squadShadowImgs   = new Map<number, Phaser.GameObjects.Image>();
  // Presentation-only — tracks when each squad last fired, used to drive a
  // short muzzle-recoil push-back on the sprite. Keyed by entity id; updated
  // on the rising edge of `attackCooldown` (prev 0 → current > 0) inside
  // syncSquads. Cleared when the squad is removed. Pure visual; never writes
  // to MatchState.
  private squadLastFireMs = new Map<number, number>();
  private squadLastCooldown = new Map<number, number>();
  // Neutral camp turrets — rendered from the camp_units atlas.
  private neutralTurretImgs = new Map<number, Phaser.GameObjects.Image>();
  // Neutral camp patrol drones — rendered from the camp_units atlas.
  // Body + ground shadow tracked independently so the shadow doesn't rotate
  // with the chassis (matches squad shadow handling).
  private neutralPatrolImgs       = new Map<number, Phaser.GameObjects.Image>();
  private neutralPatrolShadowImgs = new Map<number, Phaser.GameObjects.Image>();

  constructor(private readonly scene: Phaser.Scene) {}

  /** Returns true when all required textures are loaded. Sync methods are no-ops until then. */
  texturesReady(): boolean {
    return this.scene.textures.exists(TEX_RELAYS) && this.scene.textures.exists(TEX_HQ);
  }

  /** Returns true when the FX atlas is loaded (enables bullet sprites over Graphics fallback). */
  fxReady(): boolean {
    return this.scene.textures.exists(TEX_FX);
  }

  /** Returns true when the squad atlas is loaded (enables sprite squad bodies). */
  squadsReady(): boolean {
    return this.scene.textures.exists(TEX_SQUADS);
  }

  /** Returns true when the neutral camp atlas is loaded. */
  campReady(): boolean {
    return this.scene.textures.exists(TEX_CAMP);
  }

  // -------------------------------------------------------------------------
  // Neutral camp units — both stationary turrets and moving patrol drones
  // are rendered from the camp_units atlas, matching the visual quality of
  // player squads + buildings. Patrols are position-interpolated between
  // snapshots (same trick as syncSquads) so they glide instead of jumping
  // every tick, and rotate by `u.facing` to point in the travel direction.
  // -------------------------------------------------------------------------
  syncNeutralUnits(
    prev: MatchState | null,
    current: MatchState,
    alpha: number,
    nowMs: number = performance.now(),
    isVisible: (u: NeutralUnit, x: number, y: number) => boolean = () => true,
  ): void {
    if (!this.campReady()) return;
    const seenTurret = new Set<number>();
    const seenPatrol = new Set<number>();

    for (const u of Object.values(current.neutralUnits) as NeutralUnit[]) {
      const hpRatio = u.maxHp > 0 ? u.hp / u.maxHp : 1;
      const hpAlpha = 0.55 + hpRatio * 0.45;

      if (u.kind === 'turret') {
        // Stationary — fog test at the unit's own position.
        if (!isVisible(u, u.pos.x, u.pos.y)) continue;
        seenTurret.add(u.id);
        let img = this.neutralTurretImgs.get(u.id);
        if (!img) {
          img = this.scene.add.image(u.pos.x, u.pos.y, TEX_CAMP, 'camp_turret');
          img.setOrigin(0.5, 0.5);
          // Depth 7.6: sits above mini-turret sprites (7.5) but below buildingsGfx
          // overlay (8), so it integrates naturally into the defensive layer.
          img.setDepth(7.6);
          img.setDisplaySize(CAMP_TURRET_W, CAMP_TURRET_H);
          this.neutralTurretImgs.set(u.id, img);
        } else {
          img.setPosition(u.pos.x, u.pos.y);
        }
        img.setAlpha(hpAlpha);
        continue;
      }

      // patrol drone
      const prevUnit = prev?.neutralUnits[u.id] ?? u;
      const x = prevUnit.pos.x + (u.pos.x - prevUnit.pos.x) * alpha;
      const y = prevUnit.pos.y + (u.pos.y - prevUnit.pos.y) * alpha;
      // Fog test at the interpolated position so the sprite disappears
      // exactly when it walks out of the viewer's vision.
      if (!isVisible(u, x, y)) continue;
      seenPatrol.add(u.id);
      const facing = lerpAngle(prevUnit.facing, u.facing, alpha);

      // Subtle walk-wobble when the drone is moving (matches squad treatment).
      const dx = u.pos.x - prevUnit.pos.x;
      const dy = u.pos.y - prevUnit.pos.y;
      const moving = dx * dx + dy * dy > 0.25;
      const wobbleDeg = moving ? Math.sin(nowMs / 220 + u.id * 0.51) * 2.5 : 0;
      const angleDeg = facing * (180 / Math.PI) + wobbleDeg;

      // Ground shadow — fixed orientation, just follows the drone.
      let shadow = this.neutralPatrolShadowImgs.get(u.id);
      if (!shadow) {
        shadow = this.scene.add.image(x, y, TEX_POLISH, 'shadow_sm');
        shadow.setOrigin(0.5, 0.5);
        shadow.setDepth(9.4);  // same depth band as squad shadow
        shadow.setDisplaySize(CAMP_PATROL_W * 0.95, CAMP_PATROL_H * 0.5);
        shadow.setAlpha(0.55);
        this.neutralPatrolShadowImgs.set(u.id, shadow);
      } else {
        shadow.setPosition(x + 2, y + 4);
      }

      let img = this.neutralPatrolImgs.get(u.id);
      if (!img) {
        img = this.scene.add.image(x, y, TEX_CAMP, 'camp_patrol');
        img.setOrigin(0.5, 0.5);
        img.setDepth(9.5);  // same depth band as player squads
        img.setDisplaySize(CAMP_PATROL_W, CAMP_PATROL_H);
        this.neutralPatrolImgs.set(u.id, img);
      } else {
        img.setPosition(x, y);
      }
      img.setAngle(angleDeg);
      img.setAlpha(hpAlpha);
    }

    for (const [id, img] of this.neutralTurretImgs) {
      if (!seenTurret.has(id)) { img.destroy(); this.neutralTurretImgs.delete(id); }
    }
    for (const [id, img] of this.neutralPatrolImgs) {
      if (!seenPatrol.has(id)) { img.destroy(); this.neutralPatrolImgs.delete(id); }
    }
    for (const [id, img] of this.neutralPatrolShadowImgs) {
      if (!seenPatrol.has(id)) { img.destroy(); this.neutralPatrolShadowImgs.delete(id); }
    }
  }

  // -------------------------------------------------------------------------
  // Relay control nodes
  // -------------------------------------------------------------------------
  syncRelays(state: MatchState, nowMs: number = performance.now()): void {
    if (!this.scene.textures.exists(TEX_RELAYS)) return;
    const seen = new Set<number>();

    for (const node of Object.values(state.controlNodes)) {
      seen.add(node.id);
      const frame = relayFrame(node.ownerId, node.contested);
      // V1.5+ — idle pulse. Relays are the most important non-Core landmark;
      // a 0.4 Hz pulse (period 2.5 s) with ±3% amplitude makes them feel
      // alive without distracting from combat. Owned relays pulse a bit
      // harder (±4%) than neutral so the contested centre reads as charged.
      const ownedAmplitude = node.ownerId !== null ? 0.04 : 0.025;
      const phase = (nowMs / 1200) + node.id * 0.31;
      const scaleMul = 1 + Math.sin(phase * Math.PI * 2 * 0.4) * ownedAmplitude;

      let img = this.relayImgs.get(node.id);
      if (!img) {
        img = this.scene.add.image(node.pos.x, node.pos.y, TEX_RELAYS, frame);
        img.setOrigin(ORIGIN_X, ORIGIN_Y);
        img.setDepth(5.5);   // between coresGfx(5) and nodesGfx(6)
        img.setDisplaySize(RELAY_W * scaleMul, RELAY_H * scaleMul);
        this.relayImgs.set(node.id, img);
      } else {
        img.setTexture(TEX_RELAYS, frame);
        img.setPosition(node.pos.x, node.pos.y);
        img.setDisplaySize(RELAY_W * scaleMul, RELAY_H * scaleMul);
      }
    }

    for (const [id, img] of this.relayImgs) {
      if (!seen.has(id)) { img.destroy(); this.relayImgs.delete(id); }
    }
  }

  // -------------------------------------------------------------------------
  // HQ cores
  // -------------------------------------------------------------------------
  syncCores(state: MatchState, nowMs: number = performance.now()): void {
    if (!this.scene.textures.exists(TEX_HQ)) return;
    const seen = new Set<number>();

    for (const core of Object.values(state.cores)) {
      seen.add(core.id);
      const frame = coreFrame(core.ownerId, core.hp, core.maxHp);
      // V1.5+ — idle pulse. HQ cores breathe at 0.25 Hz (period 4 s) with
      // ±2.5% amplitude. Subtle on the fortress so it reads as "active
      // command centre" without distracting from incoming combat.
      const phase = (nowMs / 2000) + core.id * 0.41;
      const scaleMul = 1 + Math.sin(phase * Math.PI * 2 * 0.25) * 0.025;

      let img = this.coreImgs.get(core.id);
      if (!img) {
        img = this.scene.add.image(core.pos.x, core.pos.y, TEX_HQ, frame);
        img.setOrigin(ORIGIN_X, ORIGIN_Y);
        img.setDepth(4.5);   // between signalLinksGfx(4) and coresGfx(5)
        img.setDisplaySize(CORE_W * scaleMul, CORE_H * scaleMul);
        this.coreImgs.set(core.id, img);
      } else {
        img.setTexture(TEX_HQ, frame);
        img.setPosition(core.pos.x, core.pos.y);
        img.setDisplaySize(CORE_W * scaleMul, CORE_H * scaleMul);
      }
    }

    for (const [id, img] of this.coreImgs) {
      if (!seen.has(id)) { img.destroy(); this.coreImgs.delete(id); }
    }
  }

  // -------------------------------------------------------------------------
  // Buildings (permanent turrets, mini-turrets, production hubs)
  // -------------------------------------------------------------------------
  syncBuildings(state: MatchState, nowMs: number): void {
    const seen = new Set<number>();

    for (const b of Object.values(state.buildings)) {
      seen.add(b.id);
      const tex = buildingTex(b.kind);
      if (!this.scene.textures.exists(tex)) continue;

      const frame  = buildingFrame(b);
      const { w, h } = buildingSize(b.kind);
      const op      = isOperational(b);
      const offline = b.kind === 'mini_turret' && !b.isOnline;
      const alpha   = offline ? offlineMiniTurretAlpha(nowMs) : op ? 0.95 : 0.45;

      // V1.5+ — idle pulse for operational buildings. A 0.3 Hz sin wave
      // (period ~3.3 s) breathes the sprite 0.97× → 1.03×, so the world
      // isn't statue-still even when nothing's happening. Building ID
      // mixes into the phase so neighbours don't pulse in lockstep.
      let scaleMul = 1;
      if (op && !offline) {
        const phase = (nowMs / 1600) + b.id * 0.37;
        scaleMul = 1 + Math.sin(phase * Math.PI * 2 * 0.3) * 0.03;
      }

      let img = this.buildingImgs.get(b.id);
      if (!img) {
        img = this.scene.add.image(b.pos.x, b.pos.y, tex, frame);
        img.setOrigin(ORIGIN_X, ORIGIN_Y);
        img.setDepth(7.5);   // between slotGhostGfx(7) and buildingsGfx(8)
        img.setDisplaySize(w * scaleMul, h * scaleMul);
        this.buildingImgs.set(b.id, img);
      } else {
        img.setTexture(tex, frame);
        img.setPosition(b.pos.x, b.pos.y);
        img.setDisplaySize(w * scaleMul, h * scaleMul);
      }
      img.setAlpha(alpha);
    }

    for (const [id, img] of this.buildingImgs) {
      if (!seen.has(id)) { img.destroy(); this.buildingImgs.delete(id); }
    }
  }

  // -------------------------------------------------------------------------
  // Ground effects: portal aura under cores, drop shadows under relays/buildings
  // -------------------------------------------------------------------------
  syncGroundEffects(state: MatchState): void {
    if (!this.scene.textures.exists(TEX_POLISH)) return;

    // Portal glow under HQ cores (depth 3.5 — below signal links at depth 4)
    const seenCores = new Set<number>();
    for (const core of Object.values(state.cores)) {
      seenCores.add(core.id);
      const frame = core.ownerId === 1 ? 'portal_p1' : 'portal_p0';
      let img = this.coreAuraImgs.get(core.id);
      if (!img) {
        img = this.scene.add.image(core.pos.x, core.pos.y, TEX_POLISH, frame);
        img.setOrigin(0.5, 0.5);
        img.setDepth(3.5);
        img.setDisplaySize(PORTAL_W, PORTAL_H);
        this.coreAuraImgs.set(core.id, img);
      } else {
        img.setTexture(TEX_POLISH, frame);
        img.setPosition(core.pos.x, core.pos.y);
        img.setDisplaySize(PORTAL_W, PORTAL_H);
      }
    }
    for (const [id, img] of this.coreAuraImgs) {
      if (!seenCores.has(id)) { img.destroy(); this.coreAuraImgs.delete(id); }
    }

    // Drop shadow under relay control nodes (depth 4.8 — just below relay sprites at 5.5)
    const seenRelays = new Set<number>();
    const seenRelayPools = new Set<number>();
    const seenRelayRings = new Set<number>();
    for (const node of Object.values(state.controlNodes)) {
      seenRelays.add(node.id);
      let img = this.relayShadowImgs.get(node.id);
      if (!img) {
        img = this.scene.add.image(node.pos.x, node.pos.y, TEX_POLISH, 'shadow_md');
        img.setOrigin(0.5, 0.5);
        img.setDepth(4.8);
        img.setDisplaySize(RELAY_SHADOW_W, RELAY_SHADOW_H);
        this.relayShadowImgs.set(node.id, img);
      } else {
        img.setPosition(node.pos.x, node.pos.y);
      }
      // V1.8 facelift — owned relays radiate a team-color light pool +
      // a brighter captured-territory ring stamp. Both additive; both
      // depth-stack ABOVE obstacles (-500) and BELOW the relay sprite (5.5)
      // so the floor reads as illuminated while the sprite stays sharp.
      if (node.ownerId !== null) {
        seenRelayPools.add(node.id);
        seenRelayRings.add(node.id);
        const poolFrame = node.ownerId === 1 ? 'pool_p1' : 'pool_p0';
        const ringFrame = node.ownerId === 1 ? 'ring_p1' : 'ring_p0';
        let pool = this.relayPoolImgs.get(node.id);
        if (!pool) {
          pool = this.scene.add.image(node.pos.x, node.pos.y, TEX_POOLS, poolFrame);
          pool.setOrigin(0.5, 0.5);
          pool.setDepth(4.6);
          pool.setBlendMode(Phaser.BlendModes.ADD);
          pool.setDisplaySize(RELAY_POOL_W, RELAY_POOL_H);
          this.relayPoolImgs.set(node.id, pool);
        } else {
          pool.setTexture(TEX_POOLS, poolFrame);
          pool.setPosition(node.pos.x, node.pos.y);
        }
        let ring = this.relayRingImgs.get(node.id);
        if (!ring) {
          ring = this.scene.add.image(node.pos.x, node.pos.y, TEX_POOLS, ringFrame);
          ring.setOrigin(0.5, 0.5);
          ring.setDepth(4.7);
          ring.setBlendMode(Phaser.BlendModes.ADD);
          ring.setDisplaySize(RELAY_RING_W, RELAY_RING_H);
          this.relayRingImgs.set(node.id, ring);
        } else {
          ring.setTexture(TEX_POOLS, ringFrame);
          ring.setPosition(node.pos.x, node.pos.y);
        }
      }
    }
    for (const [id, img] of this.relayShadowImgs) {
      if (!seenRelays.has(id)) { img.destroy(); this.relayShadowImgs.delete(id); }
    }
    for (const [id, img] of this.relayPoolImgs) {
      if (!seenRelayPools.has(id)) { img.destroy(); this.relayPoolImgs.delete(id); }
    }
    for (const [id, img] of this.relayRingImgs) {
      if (!seenRelayRings.has(id)) { img.destroy(); this.relayRingImgs.delete(id); }
    }

    // Drop shadow under permanent buildings (depth 7.4 — below building sprites at 7.5)
    const seenBldgs = new Set<number>();
    const seenBldgPools = new Set<number>();
    for (const b of Object.values(state.buildings)) {
      if (b.kind === 'mini_turret') continue;
      seenBldgs.add(b.id);
      let img = this.buildingShadowImgs.get(b.id);
      if (!img) {
        img = this.scene.add.image(b.pos.x, b.pos.y, TEX_POLISH, 'shadow_sm');
        img.setOrigin(0.5, 0.5);
        img.setDepth(7.4);
        img.setDisplaySize(BLDG_SHADOW_W, BLDG_SHADOW_H);
        this.buildingShadowImgs.set(b.id, img);
      } else {
        img.setPosition(b.pos.x, b.pos.y);
      }
    }
    for (const [id, img] of this.buildingShadowImgs) {
      if (!seenBldgs.has(id)) { img.destroy(); this.buildingShadowImgs.delete(id); }
    }
    // V1.8 facelift — operational owned buildings get a team-color light
    // pool. Sized per building kind. mini_turrets also light up (small pool)
    // because they're the most numerous defensive emplacement; a dim pool
    // network makes a turret line read as "this is fortified territory."
    for (const b of Object.values(state.buildings)) {
      if (!isOperational(b)) continue;
      if (b.ownerId !== 0 && b.ownerId !== 1) continue;
      seenBldgPools.add(b.id);
      const poolFrame = b.ownerId === 1 ? 'pool_p1' : 'pool_p0';
      let sz = { w: TURRET_POOL_W, h: TURRET_POOL_H };
      if (b.kind === 'production_hub') sz = { w: HUB_POOL_W, h: HUB_POOL_H };
      else if (b.kind === 'mini_turret') sz = { w: MINI_POOL_W, h: MINI_POOL_H };
      let pool = this.buildingPoolImgs.get(b.id);
      if (!pool) {
        pool = this.scene.add.image(b.pos.x, b.pos.y, TEX_POOLS, poolFrame);
        pool.setOrigin(0.5, 0.5);
        // depth 7.3: above obstacles (-500), below building shadow (7.4) so
        // the shadow naturally bites into the pool at the building footprint.
        pool.setDepth(7.3);
        pool.setBlendMode(Phaser.BlendModes.ADD);
        pool.setDisplaySize(sz.w, sz.h);
        this.buildingPoolImgs.set(b.id, pool);
      } else {
        pool.setTexture(TEX_POOLS, poolFrame);
        pool.setPosition(b.pos.x, b.pos.y);
        pool.setDisplaySize(sz.w, sz.h);
      }
    }
    for (const [id, img] of this.buildingPoolImgs) {
      if (!seenBldgPools.has(id)) { img.destroy(); this.buildingPoolImgs.delete(id); }
    }
  }

  // -------------------------------------------------------------------------
  // Projectile bullet sprites (replaces Graphics dots when fx atlas is loaded)
  // Caller is responsible for fog-of-war filtering via the isVisible callback.
  //
  // V1.7: every projectile now has TWO sprites stacked — an additive tracer
  // streak BEHIND the bullet (longer, faint) and the bullet sprite itself
  // (bright core). The trail is offset half-length behind the bullet so the
  // streak reads as "where it just was". Both sprites share the angle.
  // -------------------------------------------------------------------------
  syncProjectiles(
    prev: MatchState | null,
    current: MatchState,
    alpha: number,
    isVisible: (proj: Projectile, x: number, y: number) => boolean = () => true,
  ): void {
    if (!this.scene.textures.exists(TEX_FX)) return;
    const seen = new Set<number>();

    for (const proj of Object.values(current.projectiles)) {
      const prevProj = prev?.projectiles[proj.id] ?? proj;
      const x = prevProj.pos.x + (proj.pos.x - prevProj.pos.x) * alpha;
      const y = prevProj.pos.y + (proj.pos.y - prevProj.pos.y) * alpha;
      if (!isVisible(proj, x, y)) continue;

      seen.add(proj.id);
      const angleRad = Math.atan2(proj.velocity.y, proj.velocity.x);
      const angleDeg = angleRad * (180 / Math.PI);
      const tint = proj.ownerId === 1 ? 0xff9050 : proj.ownerId === 0 ? 0x66d4ff : 0xffffff;

      // Trail (additive, behind the bullet).
      let trail = this.projectileTrails.get(proj.id);
      if (!trail) {
        trail = this.scene.add.image(x, y, TEX_FX, 'tracer_trail');
        trail.setOrigin(0.5, 0.5);
        trail.setDepth(11.5);
        trail.setBlendMode(Phaser.BlendModes.ADD);
        trail.setDisplaySize(TRACER_W, TRACER_H);
        this.projectileTrails.set(proj.id, trail);
      }
      const trailOffset = TRACER_W * 0.35;
      trail.setPosition(x - Math.cos(angleRad) * trailOffset, y - Math.sin(angleRad) * trailOffset);
      trail.setAngle(angleDeg);
      trail.setTint(tint);
      trail.setAlpha(0.75);

      let img = this.projectileImgs.get(proj.id);
      if (!img) {
        img = this.scene.add.image(x, y, TEX_FX, 'bullet');
        img.setOrigin(0.5, 0.5);
        img.setDepth(12);
        img.setBlendMode(Phaser.BlendModes.ADD);
        img.setDisplaySize(BULLET_W, BULLET_H);
        this.projectileImgs.set(proj.id, img);
      } else {
        img.setPosition(x, y);
      }
      img.setAngle(angleDeg);
      img.setTint(tint);
    }

    for (const [id, img] of this.projectileImgs) {
      if (!seen.has(id)) { img.destroy(); this.projectileImgs.delete(id); }
    }
    for (const [id, img] of this.projectileTrails) {
      if (!seen.has(id)) { img.destroy(); this.projectileTrails.delete(id); }
    }
  }

  // -------------------------------------------------------------------------
  // Squad sprite bodies (V1.7 — replaces Graphics shapes when squads atlas is loaded).
  // Caller is responsible for fog-of-war filtering via the isVisible callback.
  //
  // Each squad gets two layers: a soft drop shadow under the body, and the
  // rotated chassis sprite itself. HP bars / selection / ability rings remain
  // owned by Renderer's Graphics layer above.
  // -------------------------------------------------------------------------
  syncSquads(
    prev: MatchState | null,
    current: MatchState,
    alpha: number,
    nowMs: number = performance.now(),
    isVisible: (squad: Squad, x: number, y: number) => boolean = () => true,
  ): void {
    if (!this.scene.textures.exists(TEX_SQUADS)) return;
    const seen = new Set<number>();

    for (const squad of Object.values(current.squads)) {
      const prevSquad = prev?.squads[squad.id] ?? squad;
      const x = prevSquad.pos.x + (squad.pos.x - prevSquad.pos.x) * alpha;
      const y = prevSquad.pos.y + (squad.pos.y - prevSquad.pos.y) * alpha;
      if (!isVisible(squad, x, y)) continue;

      seen.add(squad.id);
      const frame = `${squad.archetype}_p${squad.ownerId}`;
      const facing = lerpAngle(prevSquad.facing, squad.facing, alpha);

      // V1.5+ — walk wobble. When the squad has linear velocity > a small
      // threshold, rotate the sprite ±3° on a sin wave keyed to time +
      // entity id (so neighbours don't lockstep). Stationary squads
      // simply face their target with no wobble. Velocity is approximated
      // from the previous→current snapshot delta; if prev is null, treat
      // as stationary.
      const dxStep = squad.pos.x - prevSquad.pos.x;
      const dyStep = squad.pos.y - prevSquad.pos.y;
      const movingSq = dxStep * dxStep + dyStep * dyStep;
      // dxStep/dyStep are per-tick deltas; > 0.5 px per tick ≈ actually moving.
      const isMoving = movingSq > 0.25;
      let wobbleDeg = 0;
      if (isMoving) {
        const phase = (nowMs / 220) + squad.id * 0.51;
        wobbleDeg = Math.sin(phase) * 3;
      }
      const angleDeg = facing * (180 / Math.PI) + wobbleDeg;

      // V1.5+ — muzzle recoil. Detect "just fired" from the rising edge of
      // `attackCooldown` (prev tick = 0, current > 0). Visual feedback only;
      // never writes to sim. Push the sprite back along its facing 0..4 px,
      // lerping to zero over RECOIL_MS.
      const prevCooldown = this.squadLastCooldown.get(squad.id) ?? 0;
      if (prevCooldown <= 0.0001 && squad.attackCooldown > 0.0001) {
        this.squadLastFireMs.set(squad.id, nowMs);
      }
      this.squadLastCooldown.set(squad.id, squad.attackCooldown);
      const lastFireMs = this.squadLastFireMs.get(squad.id) ?? -Infinity;
      const sinceFire = nowMs - lastFireMs;
      const RECOIL_MS = 90;
      let recoilX = 0;
      let recoilY = 0;
      if (sinceFire < RECOIL_MS) {
        const t = sinceFire / RECOIL_MS;
        const push = 4 * (1 - t);
        recoilX = -Math.cos(facing) * push;
        recoilY = -Math.sin(facing) * push;
      }
      const size = squadDisplaySize(squad);

      // Drop shadow — slightly offset down/right, doesn't rotate or wobble.
      let shadow = this.squadShadowImgs.get(squad.id);
      if (!shadow) {
        shadow = this.scene.add.image(x, y, TEX_POLISH, 'shadow_sm');
        shadow.setOrigin(0.5, 0.5);
        shadow.setDepth(9.4);   // just below squad body
        shadow.setDisplaySize(size * 0.95, size * 0.5);
        shadow.setAlpha(0.55);
        this.squadShadowImgs.set(squad.id, shadow);
      } else {
        shadow.setPosition(x + 2, y + 4);
      }

      let img = this.squadImgs.get(squad.id);
      if (!img) {
        img = this.scene.add.image(x + recoilX, y + recoilY, TEX_SQUADS, frame);
        img.setOrigin(0.5, 0.5);
        img.setDepth(9.5);   // just below Renderer.squadsGfx at depth 10
        img.setDisplaySize(size, size);
        this.squadImgs.set(squad.id, img);
      } else {
        img.setTexture(TEX_SQUADS, frame);
        img.setPosition(x + recoilX, y + recoilY);
        img.setDisplaySize(size, size);
      }
      img.setAngle(angleDeg);
    }

    for (const [id, img] of this.squadImgs) {
      if (!seen.has(id)) { img.destroy(); this.squadImgs.delete(id); }
    }
    for (const [id, img] of this.squadShadowImgs) {
      if (!seen.has(id)) { img.destroy(); this.squadShadowImgs.delete(id); }
    }
    // Trim presentation-only recoil state for squads that no longer exist.
    for (const id of this.squadLastFireMs.keys()) {
      if (!seen.has(id)) this.squadLastFireMs.delete(id);
    }
    for (const id of this.squadLastCooldown.keys()) {
      if (!seen.has(id)) this.squadLastCooldown.delete(id);
    }
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------
  dispose(): void {
    for (const img of this.relayImgs.values())         img.destroy();
    for (const img of this.coreImgs.values())          img.destroy();
    for (const img of this.buildingImgs.values())      img.destroy();
    for (const img of this.coreAuraImgs.values())      img.destroy();
    for (const img of this.relayShadowImgs.values())   img.destroy();
    for (const img of this.buildingShadowImgs.values()) img.destroy();
    for (const img of this.relayPoolImgs.values())     img.destroy();
    for (const img of this.relayRingImgs.values())     img.destroy();
    for (const img of this.buildingPoolImgs.values())  img.destroy();
    for (const img of this.projectileImgs.values())    img.destroy();
    for (const img of this.projectileTrails.values())  img.destroy();
    for (const img of this.squadImgs.values())         img.destroy();
    for (const img of this.squadShadowImgs.values())   img.destroy();
    for (const img of this.neutralTurretImgs.values())       img.destroy();
    for (const img of this.neutralPatrolImgs.values())       img.destroy();
    for (const img of this.neutralPatrolShadowImgs.values()) img.destroy();
    this.relayImgs.clear();
    this.coreImgs.clear();
    this.buildingImgs.clear();
    this.coreAuraImgs.clear();
    this.relayShadowImgs.clear();
    this.buildingShadowImgs.clear();
    this.relayPoolImgs.clear();
    this.relayRingImgs.clear();
    this.buildingPoolImgs.clear();
    this.projectileImgs.clear();
    this.projectileTrails.clear();
    this.squadImgs.clear();
    this.squadShadowImgs.clear();
    this.squadLastFireMs.clear();
    this.squadLastCooldown.clear();
    this.neutralTurretImgs.clear();
    this.neutralPatrolImgs.clear();
    this.neutralPatrolShadowImgs.clear();
  }
}

// Display diameter for a squad sprite, scaled by archetype footprint.
// V1.5+ rescale: spread the silhouettes further apart so heavy/light reads
// instantly. Recon = baseline (1.00×); Assault = 1.20× (heavier-than-it-was);
// Shield = 1.30× (the chunky tank); Engineer = 1.05× (slightly above Recon
// because of the square footprint reading lighter at the same diameter).
function squadDisplaySize(squad: Squad): number {
  switch (squad.archetype) {
    case 'assault': return SQUAD_BASE_PX * 1.20;
    case 'shield':  return SQUAD_BASE_PX * 1.30;
    case 'engineer':return SQUAD_BASE_PX * 1.05;
    default:        return SQUAD_BASE_PX * 1.00;
  }
}

function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
}
